import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

// Add this type to track modals with unique IDs
interface ModalItem {
  id: string;
  content: ReactNode;
}

/**
 * ModalContextType defines the shape of the modal context.
 * - openModal: Opens a new modal with the given content.
 * - closeModal: Closes the last opened modal, optionally after a delay or condition.
 * - closeAllModals: Closes all modals, optionally after a delay or condition.
 * - isOpen: Indicates if at least one modal is open.
 */
interface ModalContextType {
  openModal: (content: ReactNode) => void;
  closeModal: (arg?: boolean | number) => void;
  closeAllModals: (arg?: boolean | number) => void;
  isModalOpen: boolean;
}

/**
 * ModalContext provides modal management functions to its consumers.
 */
const ModalContext = createContext<ModalContextType | undefined>(undefined);

/**
 * useModal is a custom hook to access modal context.
 * Throws an error if used outside of ModalProvider.
 * @returns {ModalContextType} Modal context functions
 */
export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) throw new Error('useModal must be used within a ModalProvider');
  return context;
};

/**
 * ModalProvider manages a stack of modals and provides functions to open/close them.
 * Wrap your application with this provider to enable modal functionality.
 * @param {ReactNode} children - Child components
 * @returns {JSX.Element} Provider with modal management
 */
export const ModalProvider = ({ children }: { children: ReactNode }) => {
  // Track modals with unique id's
  const [modalStack, setModalStack] = useState<ModalItem[]>([]);

  /**
   * Indicates if at least one modal is open.
   */
  const isModalOpen = modalStack.length > 0;

  // Disable page scrolling when a modal is open
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isModalOpen]);

  // Generate unique ID for each modal
  function generateModalId(): string {
    return `modal-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Opens a new modal by adding its content to the stack.
   * @param {ReactNode} content - Modal content to display
   */
  const openModal = useCallback((content: ReactNode) => {
    const modalItem: ModalItem = {
      id: generateModalId(),
      content,
    };
    setModalStack((stack) => {
      const newStack = [...stack, modalItem];
      return newStack;
    });
  }, []);

  /**
   * Closes the last opened modal.
   * If a number is provided, closes after the specified delay (ms).
   * If false is provided, does nothing.
   * @param {boolean | number} [arg] - Condition or delay for closing
   */
  const closeModal = useCallback((arg?: boolean | number) => {
    if (typeof arg === 'number') {
      setTimeout(closeModal, arg);
      return; // wait for timeout
    }
    if (!(arg ?? true)) return; // do nothing if arg === false
    /// arg === true or undefined, we can close the last opened modal
    setModalStack((stack) => stack.slice(0, -1));
  }, []);

  /**
   * Closes all modals.
   * If a number is provided, closes after the specified delay (ms).
   * If false is provided, does nothing.
   * @param {boolean | number} [arg] - Condition or delay for closing all
   */
  const closeAllModals = useCallback((arg?: boolean | number) => {
    if (typeof arg === 'number') {
      setTimeout(closeAllModals, arg);
      return; // wait for timeout
    }
    if (!(arg ?? true)) return; // do nothing if arg === false
    /// arg === true or undefined, we can close all nmodals
    setModalStack([]);
  }, []);

  const modalsContent =
    modalStack.length > 0 ? (
      <div className="pd_modals">
        {modalStack.map((modal, idx) => {
          return (
            <div
              className="pd_modal"
              key={modal.id}
              style={{
                zIndex: 1000 + idx,
                pointerEvents: idx === modalStack.length - 1 ? 'auto' : 'none',
              }}
            >
              {modal.content}
            </div>
          );
        })}
      </div>
    ) : null;

  return (
    <ModalContext.Provider
      value={{ openModal, closeModal, closeAllModals, isModalOpen }}
    >
      {children}
      {modalsContent}
    </ModalContext.Provider>
  );
};
